import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { 
  AlertTriangle, CheckCircle, Clock, FileText, Download, 
  RefreshCw, Filter, TrendingUp, Users, Calendar, XCircle,
  DollarSign, BarChart3, Printer, AlertCircle, FileSpreadsheet, ExternalLink
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ReportsDashboard = () => {
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('coverage');
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  
  // Filters
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [dispatchView, setDispatchView] = useState('today');

  // Navigation handlers
  const navigateToWorkOrder = (workOrderId) => {
    if (workOrderId) {
      navigate(`/work-orders/${workOrderId}`);
    }
  };

  const navigateToVendor = (vendorId) => {
    if (vendorId) {
      navigate(`/vendors/${vendorId}`);
    }
  };

  const navigateToClient = (clientId) => {
    if (clientId) {
      navigate(`/clients/${clientId}`);
    }
  };

  const fetchReport = useCallback(async (reportType) => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/reports/${reportType}`;
      const params = new URLSearchParams();
      
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      if (reportType === 'daily-dispatch') params.append('view', dispatchView);
      
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await fetch(url, {
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setReportData(data);
      } else {
        toast.error('Failed to load report');
      }
    } catch (error) {
      toast.error('Error loading report');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, dateFrom, dateTo, dispatchView]);

  useEffect(() => {
    const reportMap = {
      coverage: 'open-orders-coverage',
      completion: 'completion-missing-docs',
      profitability: 'profitability',
      utilization: 'interpreter-utilization-full',
      dispatch: 'daily-dispatch',
      cancellations: 'cancellations'
    };
    fetchReport(reportMap[activeTab]);
  }, [activeTab, fetchReport]);

  const exportCSV = () => {
    if (!reportData?.data) return;
    
    const headers = Object.keys(reportData.data[0] || {});
    const csvContent = [
      headers.join(','),
      ...reportData.data.map(row => 
        headers.map(h => {
          const val = row[h];
          if (Array.isArray(val)) return `"${val.join('; ')}"`;
          if (typeof val === 'object') return `"${JSON.stringify(val)}"`;
          return `"${val || ''}"`;
        }).join(',')
      )
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${reportData.report_name.replace(/\s+/g, '_')}_${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('CSV exported');
  };

  // Map tab names to report types for export
  const getReportType = () => {
    const reportMap = {
      coverage: 'coverage',
      completion: 'completion',
      profitability: 'profitability',
      utilization: 'utilization',
      dispatch: 'dispatch',
      cancellations: 'cancellations'
    };
    return reportMap[activeTab];
  };

  const exportToCSV = async () => {
    const reportType = getReportType();
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      if (activeTab === 'dispatch') params.append('view', dispatchView);
      
      const url = `${API_URL}/api/reports/export/${reportType}/csv?${params.toString()}`;
      const response = await fetch(url, {
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.download = `${reportData?.report_name?.replace(/\s+/g, '_') || reportType}_${new Date().toISOString().slice(0,10)}.csv`;
        a.click();
        window.URL.revokeObjectURL(downloadUrl);
        toast.success('CSV exported successfully');
      } else {
        toast.error('Failed to export CSV');
      }
    } catch (error) {
      toast.error('Error exporting CSV');
    }
  };

  const exportToPDF = async () => {
    const reportType = getReportType();
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      if (activeTab === 'dispatch') params.append('view', dispatchView);
      
      const url = `${API_URL}/api/reports/export/${reportType}/pdf?${params.toString()}`;
      const response = await fetch(url, {
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.download = `${reportData?.report_name?.replace(/\s+/g, '_') || reportType}_${new Date().toISOString().slice(0,10)}.pdf`;
        a.click();
        window.URL.revokeObjectURL(downloadUrl);
        toast.success('PDF exported successfully');
      } else {
        toast.error('Failed to export PDF');
      }
    } catch (error) {
      toast.error('Error exporting PDF');
    }
  };

  const getAlertBadge = (level) => {
    const styles = {
      critical: 'bg-red-500 text-white',
      urgent: 'bg-orange-500 text-white',
      warning: 'bg-yellow-500 text-black',
      ok: 'bg-green-500 text-white'
    };
    return <Badge className={styles[level] || 'bg-gray-500'}>{level?.toUpperCase()}</Badge>;
  };

  const formatDateTime = (dt) => {
    if (!dt) return 'N/A';
    try {
      return new Date(dt).toLocaleString('en-US', {
        month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
      });
    } catch {
      return dt;
    }
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Reports Dashboard</h1>
          <p className="text-slate-600">Comprehensive analytics and insights</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => fetchReport(activeTab === 'coverage' ? 'open-orders-coverage' : activeTab)} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button variant="outline" onClick={exportToCSV} disabled={!reportData?.data?.length} data-testid="export-csv-btn">
            <FileSpreadsheet className="h-4 w-4 mr-2" />
            CSV
          </Button>
          <Button onClick={exportToPDF} disabled={!reportData?.data?.length} data-testid="export-pdf-btn">
            <Download className="h-4 w-4 mr-2" />
            PDF
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-4 flex-wrap">
            <Filter className="h-4 w-4 text-slate-500" />
            <div className="flex items-center gap-2">
              <Label className="text-sm">From:</Label>
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="w-40"
              />
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-sm">To:</Label>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="w-40"
              />
            </div>
            {activeTab === 'dispatch' && (
              <Select value={dispatchView} onValueChange={setDispatchView}>
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="today">Today</SelectItem>
                  <SelectItem value="tomorrow">Tomorrow</SelectItem>
                  <SelectItem value="this_week">This Week</SelectItem>
                </SelectContent>
              </Select>
            )}
            <Button variant="ghost" size="sm" onClick={() => { setDateFrom(''); setDateTo(''); }}>
              Clear
            </Button>
          </div>
        </CardContent>
      </Card>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-6 w-full">
          <TabsTrigger value="coverage" className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4" />
            <span className="hidden md:inline">Coverage Risk</span>
          </TabsTrigger>
          <TabsTrigger value="dispatch" className="flex items-center gap-2">
            <Calendar className="h-4 w-4" />
            <span className="hidden md:inline">Dispatch</span>
          </TabsTrigger>
          <TabsTrigger value="completion" className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            <span className="hidden md:inline">Missing Docs</span>
          </TabsTrigger>
          <TabsTrigger value="profitability" className="flex items-center gap-2">
            <DollarSign className="h-4 w-4" />
            <span className="hidden md:inline">Profitability</span>
          </TabsTrigger>
          <TabsTrigger value="utilization" className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            <span className="hidden md:inline">Utilization</span>
          </TabsTrigger>
          <TabsTrigger value="cancellations" className="flex items-center gap-2">
            <XCircle className="h-4 w-4" />
            <span className="hidden md:inline">Cancellations</span>
          </TabsTrigger>
        </TabsList>

        {/* Coverage Risk Report */}
        <TabsContent value="coverage">
          {reportData && (
            <div className="space-y-4">
              <div className="grid grid-cols-4 gap-4">
                <Card className="bg-red-50 border-red-200">
                  <CardContent className="p-4 text-center">
                    <div className="text-3xl font-bold text-red-600">{reportData.summary?.critical_alerts || 0}</div>
                    <div className="text-sm text-red-700">Critical (&lt;4h)</div>
                  </CardContent>
                </Card>
                <Card className="bg-orange-50 border-orange-200">
                  <CardContent className="p-4 text-center">
                    <div className="text-3xl font-bold text-orange-600">{reportData.summary?.urgent_alerts || 0}</div>
                    <div className="text-sm text-orange-700">Urgent (&lt;12h)</div>
                  </CardContent>
                </Card>
                <Card className="bg-yellow-50 border-yellow-200">
                  <CardContent className="p-4 text-center">
                    <div className="text-3xl font-bold text-yellow-600">{reportData.summary?.warnings || 0}</div>
                    <div className="text-sm text-yellow-700">Warning (&lt;48h)</div>
                  </CardContent>
                </Card>
                <Card className="bg-blue-50 border-blue-200">
                  <CardContent className="p-4 text-center">
                    <div className="text-3xl font-bold text-blue-600">{reportData.summary?.total_orders || 0}</div>
                    <div className="text-sm text-blue-700">Total Open</div>
                  </CardContent>
                </Card>
              </div>

              <Card>
                <CardHeader>
                  <CardTitle>Open Orders</CardTitle>
                  <CardDescription>Click on a row to view work order details</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-slate-50">
                          <th className="p-2 text-left">Alert</th>
                          <th className="p-2 text-left">WO #</th>
                          <th className="p-2 text-left">Start Time</th>
                          <th className="p-2 text-left">Hours Left</th>
                          <th className="p-2 text-left">Client</th>
                          <th className="p-2 text-left">Language</th>
                          <th className="p-2 text-left">Assigned To</th>
                          <th className="p-2 text-left">Status</th>
                          <th className="p-2 text-left"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {reportData.data?.map((row, i) => (
                          <tr 
                            key={i} 
                            className={`border-b hover:bg-slate-100 cursor-pointer transition-colors ${row.alert_level === 'critical' ? 'bg-red-50 hover:bg-red-100' : row.alert_level === 'urgent' ? 'bg-orange-50 hover:bg-orange-100' : ''}`}
                            onClick={() => navigateToWorkOrder(row.work_order_id)}
                            data-testid={`coverage-row-${i}`}
                          >
                            <td className="p-2">{getAlertBadge(row.alert_level)}</td>
                            <td className="p-2 font-mono text-blue-600 hover:underline">{row.work_order_number || row.work_order_id}</td>
                            <td className="p-2">{formatDateTime(row.start_datetime)}</td>
                            <td className="p-2">
                              {row.hours_until_start !== null ? (
                                <span className={row.hours_until_start < 4 ? 'text-red-600 font-bold' : ''}>
                                  {row.hours_until_start}h
                                </span>
                              ) : 'N/A'}
                            </td>
                            <td className="p-2">{row.client_name}</td>
                            <td className="p-2">{row.languages?.join(', ')}</td>
                            <td className="p-2">{row.assigned_vendor || <span className="text-red-600">UNASSIGNED</span>}</td>
                            <td className="p-2"><Badge variant="outline">{row.status}</Badge></td>
                            <td className="p-2"><ExternalLink className="h-4 w-4 text-slate-400" /></td>
                          </tr>
                        ))}
                        {!reportData.data?.length && (
                          <tr><td colSpan="9" className="p-8 text-center text-slate-500">No open orders</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>

        {/* Daily Dispatch */}
        <TabsContent value="dispatch">
          {reportData && (
            <div className="space-y-4">
              <Card className="bg-blue-50">
                <CardContent className="p-4 flex justify-between items-center">
                  <div>
                    <h3 className="text-xl font-bold">{reportData.report_name}</h3>
                    <p className="text-sm text-slate-600">
                      {reportData.summary?.total_assignments || 0} assignments | 
                      {reportData.summary?.unassigned || 0} unassigned
                    </p>
                  </div>
                  <Button variant="outline" onClick={() => window.print()}>
                    <Printer className="h-4 w-4 mr-2" /> Print
                  </Button>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardDescription>Click on a row to view work order details</CardDescription>
                </CardHeader>
                <CardContent className="p-0">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-slate-50">
                        <th className="p-3 text-left">Time</th>
                        <th className="p-3 text-left">Interpreter</th>
                        <th className="p-3 text-left">Client</th>
                        <th className="p-3 text-left">Location</th>
                        <th className="p-3 text-left">Contact</th>
                        <th className="p-3 text-left">Languages</th>
                        <th className="p-3 text-left">Notes</th>
                        <th className="p-3 text-left"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {reportData.data?.map((row, i) => (
                        <tr 
                          key={i} 
                          className={`border-b cursor-pointer transition-colors ${!row.is_assigned ? 'bg-red-50 hover:bg-red-100' : 'hover:bg-slate-100'}`}
                          onClick={() => navigateToWorkOrder(row.work_order_id)}
                          data-testid={`dispatch-row-${i}`}
                        >
                          <td className="p-3">
                            <div className="font-medium text-blue-600">{formatDateTime(row.start_datetime)}</div>
                            <div className="text-xs text-slate-500">to {formatDateTime(row.end_datetime)}</div>
                          </td>
                          <td className="p-3">
                            <div className={!row.is_assigned ? 'text-red-600 font-bold' : 'font-medium'}>
                              {row.interpreter}
                            </div>
                            {row.interpreter_phone && <div className="text-xs">{row.interpreter_phone}</div>}
                          </td>
                          <td className="p-3">{row.client_name}</td>
                          <td className="p-3">
                            <Badge variant="outline" className="mr-1">{row.modality}</Badge>
                            <span className="text-xs">{row.location?.substring(0, 30)}</span>
                          </td>
                          <td className="p-3">
                            <div>{row.contact_person}</div>
                            <div className="text-xs">{row.contact_phone}</div>
                          </td>
                          <td className="p-3">{row.languages?.join(', ')}</td>
                          <td className="p-3 text-xs">{row.special_instructions?.substring(0, 50)}</td>
                          <td className="p-3"><ExternalLink className="h-4 w-4 text-slate-400" /></td>
                        </tr>
                      ))}
                      {!reportData.data?.length && (
                        <tr><td colSpan="8" className="p-8 text-center text-slate-500">No assignments scheduled</td></tr>
                      )}
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>

        {/* Completion & Missing Docs */}
        <TabsContent value="completion">
          {reportData && (
            <div className="space-y-4">
              <div className="grid grid-cols-4 gap-4">
                <Card>
                  <CardContent className="p-4 text-center">
                    <div className="text-3xl font-bold text-slate-900">{reportData.summary?.total_completed_jobs || 0}</div>
                    <div className="text-sm text-slate-500">Total Jobs</div>
                  </CardContent>
                </Card>
                <Card className="bg-green-50">
                  <CardContent className="p-4 text-center">
                    <div className="text-3xl font-bold text-green-600">{reportData.summary?.fully_documented || 0}</div>
                    <div className="text-sm text-green-700">Fully Documented</div>
                  </CardContent>
                </Card>
                <Card className="bg-amber-50">
                  <CardContent className="p-4 text-center">
                    <div className="text-3xl font-bold text-amber-600">{reportData.summary?.missing_docs || 0}</div>
                    <div className="text-sm text-amber-700">Missing Docs</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <div className="text-3xl font-bold text-blue-600">{reportData.summary?.completion_rate || 0}%</div>
                    <div className="text-sm text-slate-500">Completion Rate</div>
                  </CardContent>
                </Card>
              </div>

              <Card>
                <CardHeader>
                  <CardTitle>Jobs with Missing Documents</CardTitle>
                  <CardDescription>
                    Missing: {reportData.summary?.missing_breakdown?.encounter_form || 0} forms, 
                    {reportData.summary?.missing_breakdown?.client_signature || 0} client sigs, 
                    {reportData.summary?.missing_breakdown?.invoice || 0} invoices | Click row to view details
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-slate-50">
                        <th className="p-2 text-left">WO #</th>
                        <th className="p-2 text-left">Client</th>
                        <th className="p-2 text-left">Vendor</th>
                        <th className="p-2 text-left">Completed</th>
                        <th className="p-2 text-left">Missing Items</th>
                        <th className="p-2 text-left">Status</th>
                        <th className="p-2 text-left"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {reportData.data?.filter(r => !r.is_complete).map((row, i) => (
                        <tr 
                          key={i} 
                          className="border-b hover:bg-slate-100 cursor-pointer transition-colors"
                          onClick={() => navigateToWorkOrder(row.work_order_id)}
                          data-testid={`completion-row-${i}`}
                        >
                          <td className="p-2 font-mono text-blue-600 hover:underline">{row.work_order_number || row.work_order_id}</td>
                          <td className="p-2">{row.client_name}</td>
                          <td className="p-2">{row.vendor_name || 'N/A'}</td>
                          <td className="p-2">{row.completed_at?.substring(0, 10)}</td>
                          <td className="p-2">
                            {row.missing_items?.map((item, j) => (
                              <Badge key={j} variant="destructive" className="mr-1 mb-1">{item}</Badge>
                            ))}
                          </td>
                          <td className="p-2">
                            {row.is_complete ? (
                              <CheckCircle className="h-4 w-4 text-green-500" />
                            ) : (
                              <AlertCircle className="h-4 w-4 text-amber-500" />
                            )}
                          </td>
                          <td className="p-2"><ExternalLink className="h-4 w-4 text-slate-400" /></td>
                        </tr>
                      ))}
                      {reportData.data?.filter(r => !r.is_complete).length === 0 && (
                        <tr><td colSpan="7" className="p-8 text-center text-green-600">All jobs fully documented!</td></tr>
                      )}
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>

        {/* Profitability */}
        <TabsContent value="profitability">
          {reportData && (
            <div className="space-y-4">
              <div className="grid grid-cols-4 gap-4">
                <Card>
                  <CardContent className="p-4 text-center">
                    <div className="text-3xl font-bold text-slate-900">${reportData.summary?.total_revenue?.toLocaleString() || 0}</div>
                    <div className="text-sm text-slate-500">Total Revenue</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <div className="text-3xl font-bold text-slate-900">${reportData.summary?.total_cost?.toLocaleString() || 0}</div>
                    <div className="text-sm text-slate-500">Total Cost</div>
                  </CardContent>
                </Card>
                <Card className={reportData.summary?.overall_margin_pct >= 30 ? 'bg-green-50' : 'bg-red-50'}>
                  <CardContent className="p-4 text-center">
                    <div className={`text-3xl font-bold ${reportData.summary?.overall_margin_pct >= 30 ? 'text-green-600' : 'text-red-600'}`}>
                      {reportData.summary?.overall_margin_pct || 0}%
                    </div>
                    <div className="text-sm">Overall Margin</div>
                  </CardContent>
                </Card>
                <Card className="bg-amber-50">
                  <CardContent className="p-4 text-center">
                    <div className="text-3xl font-bold text-amber-600">{reportData.summary?.below_target_count || 0}</div>
                    <div className="text-sm text-amber-700">Below Target (&lt;30%)</div>
                  </CardContent>
                </Card>
              </div>

              <Card>
                <CardHeader>
                  <CardTitle>Job Profitability</CardTitle>
                  <CardDescription>Click row to view work order details</CardDescription>
                </CardHeader>
                <CardContent>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-slate-50">
                        <th className="p-2 text-left">WO #</th>
                        <th className="p-2 text-left">Client</th>
                        <th className="p-2 text-left">Languages</th>
                        <th className="p-2 text-right">Revenue</th>
                        <th className="p-2 text-right">Cost</th>
                        <th className="p-2 text-right">Margin</th>
                        <th className="p-2 text-right">Margin %</th>
                        <th className="p-2 text-left"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {reportData.data?.map((row, i) => (
                        <tr 
                          key={i} 
                          className={`border-b cursor-pointer transition-colors ${row.is_below_target ? 'bg-red-50 hover:bg-red-100' : 'hover:bg-slate-100'}`}
                          onClick={() => navigateToWorkOrder(row.work_order_id)}
                          data-testid={`profit-row-${i}`}
                        >
                          <td className="p-2 font-mono text-blue-600 hover:underline">{row.work_order_number || row.work_order_id}</td>
                          <td className="p-2">{row.client_name}</td>
                          <td className="p-2">{row.languages?.join(', ')}</td>
                          <td className="p-2 text-right">${row.revenue?.toLocaleString()}</td>
                          <td className="p-2 text-right">${row.vendor_cost?.toLocaleString()}</td>
                          <td className="p-2 text-right font-medium">${row.gross_margin?.toLocaleString()}</td>
                          <td className={`p-2 text-right font-bold ${row.margin_pct >= 30 ? 'text-green-600' : 'text-red-600'}`}>
                            {row.margin_pct}%
                          </td>
                          <td className="p-2"><ExternalLink className="h-4 w-4 text-slate-400" /></td>
                        </tr>
                      ))}
                      {!reportData.data?.length && (
                        <tr><td colSpan="8" className="p-8 text-center text-slate-500">No completed jobs found</td></tr>
                      )}
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>

        {/* Utilization */}
        <TabsContent value="utilization">
          {reportData && (
            <div className="space-y-4">
              <div className="grid grid-cols-4 gap-4">
                <Card>
                  <CardContent className="p-4 text-center">
                    <div className="text-3xl font-bold text-slate-900">{reportData.summary?.total_interpreters || 0}</div>
                    <div className="text-sm text-slate-500">Active Interpreters</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <div className="text-3xl font-bold text-blue-600">{reportData.summary?.total_offers_sent || 0}</div>
                    <div className="text-sm text-slate-500">Offers Sent</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <div className="text-3xl font-bold text-green-600">{reportData.summary?.total_assignments || 0}</div>
                    <div className="text-sm text-slate-500">Total Assignments</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <div className="text-3xl font-bold text-purple-600">{reportData.summary?.avg_acceptance_rate || 0}%</div>
                    <div className="text-sm text-slate-500">Avg Acceptance Rate</div>
                  </CardContent>
                </Card>
              </div>

              <Card>
                <CardHeader>
                  <CardTitle>Interpreter Performance</CardTitle>
                  <CardDescription>Last 30 days | Click row to view vendor details</CardDescription>
                </CardHeader>
                <CardContent>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-slate-50">
                        <th className="p-2 text-left">Interpreter</th>
                        <th className="p-2 text-left">Type</th>
                        <th className="p-2 text-center">Offers</th>
                        <th className="p-2 text-center">Accepted</th>
                        <th className="p-2 text-center">Accept %</th>
                        <th className="p-2 text-center">Assignments</th>
                        <th className="p-2 text-center">Completed</th>
                        <th className="p-2 text-center">Cancel %</th>
                        <th className="p-2 text-left"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {reportData.data?.map((row, i) => (
                        <tr 
                          key={i} 
                          className="border-b hover:bg-slate-100 cursor-pointer transition-colors"
                          onClick={() => navigateToVendor(row.vendor_id)}
                          data-testid={`utilization-row-${i}`}
                        >
                          <td className="p-2 font-medium text-blue-600 hover:underline">{row.vendor_name}</td>
                          <td className="p-2"><Badge variant="outline">{row.vendor_type}</Badge></td>
                          <td className="p-2 text-center">{row.total_offers}</td>
                          <td className="p-2 text-center">{row.accepted_offers}</td>
                          <td className={`p-2 text-center font-medium ${row.acceptance_rate >= 70 ? 'text-green-600' : row.acceptance_rate >= 50 ? 'text-yellow-600' : 'text-red-600'}`}>
                            {row.acceptance_rate}%
                          </td>
                          <td className="p-2 text-center">{row.total_assignments}</td>
                          <td className="p-2 text-center">{row.completed_assignments}</td>
                          <td className={`p-2 text-center ${row.cancel_rate > 10 ? 'text-red-600' : ''}`}>
                            {row.cancel_rate}%
                          </td>
                          <td className="p-2"><ExternalLink className="h-4 w-4 text-slate-400" /></td>
                        </tr>
                      ))}
                      {!reportData.data?.length && (
                        <tr><td colSpan="9" className="p-8 text-center text-slate-500">No interpreter data</td></tr>
                      )}
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>

        {/* Cancellations */}
        <TabsContent value="cancellations">
          {reportData && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <Card>
                  <CardContent className="p-4 text-center">
                    <div className="text-3xl font-bold text-slate-900">{reportData.summary?.total_cancellations || 0}</div>
                    <div className="text-sm text-slate-500">Total Cancellations</div>
                  </CardContent>
                </Card>
                <Card className="bg-red-50">
                  <CardContent className="p-4 text-center">
                    <div className="text-3xl font-bold text-red-600">{reportData.summary?.late_cancels || 0}</div>
                    <div className="text-sm text-red-700">Late Cancels (&lt;24h)</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <div className="text-3xl font-bold text-amber-600">{reportData.summary?.late_cancel_rate || 0}%</div>
                    <div className="text-sm text-slate-500">Late Cancel Rate</div>
                  </CardContent>
                </Card>
              </div>

              {reportData.breakdowns?.by_client?.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle>Cancellations by Client</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-2">
                      {reportData.breakdowns.by_client.map((c, i) => (
                        <Badge key={i} variant="outline" className="text-sm">
                          {c.name}: {c.count}
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              <Card>
                <CardHeader>
                  <CardTitle>Recent Cancellations</CardTitle>
                  <CardDescription>Click row to view work order details</CardDescription>
                </CardHeader>
                <CardContent>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-slate-50">
                        <th className="p-2 text-left">WO #</th>
                        <th className="p-2 text-left">Client</th>
                        <th className="p-2 text-left">Cancelled</th>
                        <th className="p-2 text-left">Scheduled For</th>
                        <th className="p-2 text-left">Hours Before</th>
                        <th className="p-2 text-left">Reason</th>
                        <th className="p-2 text-left">Fee?</th>
                        <th className="p-2 text-left"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {reportData.data?.map((row, i) => (
                        <tr 
                          key={i} 
                          className={`border-b cursor-pointer transition-colors ${row.is_late_cancel ? 'bg-red-50 hover:bg-red-100' : 'hover:bg-slate-100'}`}
                          onClick={() => navigateToWorkOrder(row.work_order_id)}
                          data-testid={`cancel-row-${i}`}
                        >
                          <td className="p-2 font-mono text-blue-600 hover:underline">{row.work_order_number || row.work_order_id}</td>
                          <td className="p-2">{row.client_name}</td>
                          <td className="p-2">{row.cancelled_at?.substring(0, 10)}</td>
                          <td className="p-2">{formatDateTime(row.scheduled_start)}</td>
                          <td className={`p-2 ${row.is_late_cancel ? 'text-red-600 font-bold' : ''}`}>
                            {row.hours_before_start !== null ? `${row.hours_before_start}h` : 'N/A'}
                          </td>
                          <td className="p-2">{row.cancel_reason}</td>
                          <td className="p-2">
                            {row.fee_applicable ? (
                              <Badge variant="destructive">Fee Applies</Badge>
                            ) : (
                              <Badge variant="outline">No Fee</Badge>
                            )}
                          </td>
                          <td className="p-2"><ExternalLink className="h-4 w-4 text-slate-400" /></td>
                        </tr>
                      ))}
                      {!reportData.data?.length && (
                        <tr><td colSpan="8" className="p-8 text-center text-green-600">No cancellations</td></tr>
                      )}
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ReportsDashboard;
